/**
 * BulkUploadModal Component
 * Modal for bulk uploading diagnostic form results via CSV
 *
 * Two-step flow:
 * 1. Upload: Select CSV file, choose mode, upload
 * 2. Results: View summary of successes and failures
 */

import React, { useState, useRef } from "react";
import { useBulkUploadDiagnosticResultsMutation } from "../../../services/api";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5045/api/v1";

const IconUpload = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 16 12 12 8 16" />
    <line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);

const IconDownload = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconAlert = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Object} props.template - { _id, title }
 * @param {string} props.companyId
 * @param {Function} props.onClose
 */
const BulkUploadModal = ({ isOpen, template, companyId, onClose }) => {
  const [step, setStep] = useState("upload"); // 'upload' | 'results'
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("all_or_nothing");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const [bulkUpload, { isLoading }] = useBulkUploadDiagnosticResultsMutation();

  if (!isOpen || !template) return null;

  const handleDownloadTemplate = async () => {
    try {
      const url = `${API_URL}/diagnostic/submissions/bulk-upload/template/${template._id}`;
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to download template");
      const blob = await response.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `diagnostic-bulk-template-${template._id}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setError("Failed to download CSV template. Please try again.");
    }
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (selected) {
      if (!selected.name.endsWith(".csv")) {
        setError("Please select a CSV file");
        return;
      }
      setFile(selected);
      setError(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) {
      if (!dropped.name.endsWith(".csv")) {
        setError("Please drop a CSV file");
        return;
      }
      setFile(dropped);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setError(null);

    try {
      const res = await bulkUpload({
        templateId: template._id,
        companyId,
        file,
        mode,
      }).unwrap();

      setResult(res);
      setStep("results");
    } catch (err) {
      const msg = err?.data?.message || err?.message || "Upload failed. Please try again.";
      setError(msg);
    }
  };

  const handleReset = () => {
    setStep("upload");
    setFile(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <div className="cd-modal-overlay" onClick={handleClose}>
      <div className="cd-modal-content cd-modal-lg" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="cd-modal-header">
          <h2 className="cd-modal-title">
            Bulk Upload Results — {template.title}
          </h2>
          <button className="cd-modal-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        <div className="cd-modal-body">
          {step === "upload" && (
            <>
              {/* Download Template */}
              <div style={{ marginBottom: "20px" }}>
                <p style={{ color: "#6b7280", fontSize: "14px", marginBottom: "12px" }}>
                  Download the CSV template, fill in the diagnostic results, then upload it below.
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 16px",
                    background: "#f3f4f6",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    color: "#374151",
                  }}
                >
                  <IconDownload /> Download CSV Template
                </button>
              </div>

              {/* Drop Zone */}
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: "2px dashed #d1d5db",
                  borderRadius: "8px",
                  padding: "40px 20px",
                  textAlign: "center",
                  cursor: "pointer",
                  background: file ? "#f0fdf4" : "#fafafa",
                  transition: "all 0.2s",
                  marginBottom: "16px",
                }}
              >
                <input
                  type="file"
                  accept=".csv"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                {file ? (
                  <div>
                    <div style={{ color: "#16a34a", marginBottom: "8px" }}>
                      <IconCheck />
                    </div>
                    <p style={{ color: "#16a34a", fontWeight: 500, fontSize: "14px" }}>
                      {file.name}
                    </p>
                    <p style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px" }}>
                      {(file.size / 1024).toFixed(1)} KB — Click or drop to replace
                    </p>
                  </div>
                ) : (
                  <div>
                    <div style={{ color: "#9ca3af", marginBottom: "8px" }}>
                      <IconUpload />
                    </div>
                    <p style={{ color: "#6b7280", fontSize: "14px" }}>
                      Drop a CSV file here, or click to browse
                    </p>
                  </div>
                )}
              </div>

              {/* Mode Selector */}
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: "#374151", marginBottom: "8px" }}>
                  Error handling mode
                </label>
                <div style={{ display: "flex", gap: "16px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#4b5563", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="mode"
                      value="all_or_nothing"
                      checked={mode === "all_or_nothing"}
                      onChange={() => setMode("all_or_nothing")}
                    />
                    All or nothing
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#4b5563", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="mode"
                      value="skip_errors"
                      checked={mode === "skip_errors"}
                      onChange={() => setMode("skip_errors")}
                    />
                    Skip invalid rows
                  </label>
                </div>
                <p style={{ fontSize: "12px", color: "#9ca3af", marginTop: "4px" }}>
                  {mode === "all_or_nothing"
                    ? "If any row has errors, the entire upload is rejected."
                    : "Valid rows will be imported; invalid rows will be skipped and reported."}
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div style={{
                  padding: "10px 14px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "6px",
                  color: "#dc2626",
                  fontSize: "13px",
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}>
                  <IconAlert /> {error}
                </div>
              )}
            </>
          )}

          {step === "results" && result && (
            <>
              {/* Summary */}
              <div style={{
                display: "flex",
                gap: "16px",
                marginBottom: "20px",
              }}>
                <div style={{
                  flex: 1,
                  padding: "16px",
                  background: "#f9fafb",
                  borderRadius: "8px",
                  textAlign: "center",
                }}>
                  <div style={{ fontSize: "24px", fontWeight: 600, color: "#374151" }}>
                    {result.summary.totalRows}
                  </div>
                  <div style={{ fontSize: "13px", color: "#6b7280" }}>Total Rows</div>
                </div>
                <div style={{
                  flex: 1,
                  padding: "16px",
                  background: result.summary.successCount > 0 ? "#f0fdf4" : "#f9fafb",
                  borderRadius: "8px",
                  textAlign: "center",
                }}>
                  <div style={{ fontSize: "24px", fontWeight: 600, color: "#16a34a" }}>
                    {result.summary.successCount}
                  </div>
                  <div style={{ fontSize: "13px", color: "#6b7280" }}>Succeeded</div>
                </div>
                <div style={{
                  flex: 1,
                  padding: "16px",
                  background: result.summary.failedCount > 0 ? "#fef2f2" : "#f9fafb",
                  borderRadius: "8px",
                  textAlign: "center",
                }}>
                  <div style={{ fontSize: "24px", fontWeight: 600, color: result.summary.failedCount > 0 ? "#dc2626" : "#374151" }}>
                    {result.summary.failedCount}
                  </div>
                  <div style={{ fontSize: "13px", color: "#6b7280" }}>Failed</div>
                </div>
              </div>

              {/* Success Message */}
              {result.summary.successCount > 0 && (
                <div style={{
                  padding: "10px 14px",
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: "6px",
                  color: "#16a34a",
                  fontSize: "13px",
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}>
                  <IconCheck /> {result.summary.successCount} diagnostic result(s) imported successfully.
                </div>
              )}

              {/* Failed Rows Table */}
              {result.failedRows && result.failedRows.length > 0 && (
                <div>
                  <h3 style={{ fontSize: "14px", fontWeight: 600, color: "#374151", marginBottom: "8px" }}>
                    Failed Rows
                  </h3>
                  <div style={{ maxHeight: "300px", overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: "6px" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                      <thead>
                        <tr style={{ background: "#f9fafb", position: "sticky", top: 0 }}>
                          <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #e5e7eb", fontWeight: 500 }}>Row</th>
                          <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #e5e7eb", fontWeight: 500 }}>Assessor</th>
                          <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #e5e7eb", fontWeight: 500 }}>Target</th>
                          <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #e5e7eb", fontWeight: 500 }}>Errors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.failedRows.map((row, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                            <td style={{ padding: "8px 12px", color: "#6b7280" }}>{row.row}</td>
                            <td style={{ padding: "8px 12px", color: "#374151" }}>{row.assessorEmail || "—"}</td>
                            <td style={{ padding: "8px 12px", color: "#374151" }}>{row.targetEmail || "—"}</td>
                            <td style={{ padding: "8px 12px", color: "#dc2626" }}>
                              {row.errors.map((e, j) => (
                                <div key={j}>{e}</div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="cd-modal-footer">
          {step === "upload" && (
            <>
              <button className="cd-modal-btn cd-modal-btn-cancel" onClick={handleClose}>
                Cancel
              </button>
              <button
                className="cd-modal-btn cd-modal-btn-confirm"
                onClick={handleUpload}
                disabled={!file || isLoading}
              >
                {isLoading ? "Uploading..." : "Upload & Process"}
              </button>
            </>
          )}
          {step === "results" && (
            <>
              <button className="cd-modal-btn cd-modal-btn-cancel" onClick={handleClose}>
                Close
              </button>
              <button className="cd-modal-btn cd-modal-btn-confirm" onClick={handleReset}>
                Upload Another
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
